import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const ContactSubmissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await api.get('/admin/contact-submissions');
      setSubmissions(response.data || []);
    } catch (err) {
      setError('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/admin/contact-submissions/${id}/read`);
      fetchSubmissions();
    } catch (err) {
      setError('Failed to mark as read');
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !sub.is_read;
    if (filter === 'read') return sub.is_read;
    return true;
  });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Contact Form Submissions</h1>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        {/* Filter Tabs */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setFilter('all')}
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            >
              All ({submissions.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`btn ${filter === 'unread' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Unread ({submissions.filter(s => !s.is_read).length})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`btn ${filter === 'read' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Read ({submissions.filter(s => s.is_read).length})
            </button>
          </div>
        </div>

        {filteredSubmissions.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)', padding: '40px' }}>
              No contact submissions yet.
            </p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission) => (
                  <tr
                    key={submission.id}
                    style={{
                      backgroundColor: submission.is_read ? 'transparent' : '#f0f8ff',
                      fontWeight: submission.is_read ? 'normal' : '600'
                    }}
                  >
                    <td>
                      {new Date(submission.created_at).toLocaleDateString()}
                      <br />
                      <small style={{ color: 'var(--secondary-color)' }}>
                        {new Date(submission.created_at).toLocaleTimeString()}
                      </small>
                    </td>
                    <td>{submission.name}</td>
                    <td>
                      <a href={`mailto:${submission.email}`}>{submission.email}</a>
                    </td>
                    <td>{submission.subject || 'No subject'}</td>
                    <td>
                      <span className={`status-badge ${submission.is_read ? 'status-published' : 'status-draft'}`}>
                        {submission.is_read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => setSelectedSubmission(submission)}
                          className="btn btn-secondary"
                        >
                          View
                        </button>
                        {!submission.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(submission.id)}
                            className="btn btn-primary"
                          >
                            Mark Read
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View Submission Modal */}
        {selectedSubmission && (
          <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Contact Submission Details</h2>

              <div style={{ marginBottom: '20px' }}>
                <p><strong>From:</strong> {selectedSubmission.name}</p>
                <p><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a></p>
                <p><strong>Subject:</strong> {selectedSubmission.subject || 'No subject'}</p>
                <p>
                  <strong>Date:</strong> {new Date(selectedSubmission.created_at).toLocaleString()}
                </p>
                <p>
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${selectedSubmission.is_read ? 'status-published' : 'status-draft'}`}>
                    {selectedSubmission.is_read ? 'Read' : 'Unread'}
                  </span>
                </p>
              </div>

              <div className="card" style={{ backgroundColor: '#f8f9fa' }}>
                <h3>Message:</h3>
                <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                  {selectedSubmission.message}
                </p>
              </div>

              <div className="button-group" style={{ marginTop: '20px' }}>
                <a
                  href={`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject || 'Your inquiry'}`}
                  className="btn btn-primary"
                >
                  Reply via Email
                </a>
                {!selectedSubmission.is_read && (
                  <button
                    onClick={() => {
                      handleMarkAsRead(selectedSubmission.id);
                      setSelectedSubmission(null);
                    }}
                    className="btn btn-success"
                  >
                    Mark as Read
                  </button>
                )}
                <button onClick={() => setSelectedSubmission(null)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSubmissions;
